import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { TypeOfTreatmentEntity } from '../../../../entities/mdqx/doctor/type-of-treatment.entity';
import { GraphQlEffectService } from '../../../../services/graph-ql-effect.service';
import { HttpService } from '../../../../services/http.service';
import { BaseActionTypes } from '../../../actions/base.actions';
import { GetDoctorsRequest } from '../../../actions/mdqx/doctor/doctor.actions';
import { TypeOfTreatmentActions } from '../../../actions/mdqx/doctor/type-of-treatment.actions';
import { State } from '../../../index';
import { getToken } from '../../../selectors/base.selectors';

enum ErrorCodes {
    Load = 'Behandlungsart Abrufen fehlgeschlagen',
    Modify = 'Behandlungsart Bearbeiten fehlgeschlagen',
    Remove = 'Behandlungsart Löschen fehlgeschlagen',
    Add = 'Behandlungsart Hinzufügen fehlgeschlagen',
}

@Injectable()
export class TypeOfTreatmentEffects {
    errors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TypeOfTreatmentActions.loadFailure, TypeOfTreatmentActions.putFailure, TypeOfTreatmentActions.changeFailure, TypeOfTreatmentActions.deleteFailure),
            map((action) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: action.ToasterMessage,
                        Err: action.Errors,
                    },
                }),
            ),
        ),
    );

    Get$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(TypeOfTreatmentActions.loadRequest),
                // map(() => {
                //     return TypeOfTreatmentActions.loadSuccess({Payload: [
                //             TypeOfTreatmentEntity.fromBackend({id: 1, name: 'q1'}),
                //             TypeOfTreatmentEntity.fromBackend({id: 2, name: 'q2'}),
                //         ]})
                // })
                withLatestFrom(this.store$.select(getToken)),
                switchMap(([{ Payload }, token]) => {
                    const queryString = `query {
                    typeoftreatment{
                        ${TypeOfTreatmentEntity.GqlFields.join(',')}
                    }
                }`;
                    return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }, '', environment.mdqxGatewayApiUrl).pipe(
                        tap((res) => {
                            this.store$.dispatch(
                                res && res.typeoftreatment
                                    ? TypeOfTreatmentActions.loadSuccess({
                                          Payload: res.typeoftreatment.map((val) => TypeOfTreatmentEntity.fromBackend(val)),
                                      })
                                    : TypeOfTreatmentActions.loadFailure({ ToasterMessage: ErrorCodes.Load, Errors: 'wrong response typeOfTreatment' }),
                            );
                        }),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                TypeOfTreatmentActions.loadFailure({
                                    ToasterMessage: ErrorCodes.Load,
                                    Errors: err,
                                }),
                            );
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    Put$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TypeOfTreatmentActions.changeRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  changeTypeoftreatment(id: ${Payload.id} name: "${Payload.name}"){
                  ${TypeOfTreatmentEntity.GqlFields.join(',')}
                  }
                }
                `;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (res?.changeTypeoftreatment) {
                            return TypeOfTreatmentActions.changeSuccess({ Payload: TypeOfTreatmentEntity.fromBackend(res.changeTypeoftreatment) });
                        } else {
                            return TypeOfTreatmentActions.changeFailure({
                                ToasterMessage: ErrorCodes.Modify,
                                Errors: 'wrong response changeTypeOfTreatment',
                            });
                        }
                    }),
                    catchError((err, caught) => {
                        return of(
                            TypeOfTreatmentActions.changeFailure({
                                ToasterMessage: ErrorCodes.Modify,
                                Errors: err,
                            }),
                        );
                    }),
                );
            }),
        ),
    );
    Delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TypeOfTreatmentActions.deleteRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  deleteTypeoftreatment(id: ${Payload.id})
                }
                `;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    concatMap((res) => {
                        if (res?.deleteTypeoftreatment) {
                            return [TypeOfTreatmentActions.deleteSuccess({ Payload: Payload.id }), new GetDoctorsRequest()];
                        } else {
                            return [
                                TypeOfTreatmentActions.deleteFailure({
                                    ToasterMessage: ErrorCodes.Remove,
                                    Errors: 'wrong response deleteTypeOfTreatment',
                                }),
                            ];
                        }
                    }),
                    catchError((err, caught) => {
                        return of(
                            TypeOfTreatmentActions.deleteFailure({
                                ToasterMessage: ErrorCodes.Remove,
                                Errors: err,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    constructor(private actions$: Actions, private store$: Store<State>, private gatewayHttpService: HttpService, private graphQlEffectService: GraphQlEffectService) {}
}
